import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FlowCase } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function CaseInbox() {
  const { persona } = useAuth();
  const [flowCode, setFlowCode] = useState('');
  const canSee = (persona?.roles.includes('CASE_OFFICER') || persona?.roles.includes('CASE_SUPERVISOR')) ?? false;

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (flowCode) p.set('flowCode', flowCode);
    return `/api/v1/flow/inbox?${p}`;
  }, [flowCode]);
  const listQ = useApiQuery<{ items: FlowCase[] }>(canSee ? query : null);

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Cases" subtitle="Your assigned cases, and unassigned cases routed to your roles" />
        <EmptyState
          title="No case-officer access"
          hint="This queue needs the CASE_OFFICER or CASE_SUPERVISOR role. Ask HR or your MDA administrator."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Cases" subtitle="Your assigned cases, and unassigned cases routed to your roles" />
      <div className="mb-4">
        <input
          className="field-input max-w-xs"
          placeholder="Filter by flow code (e.g. business_licence)"
          value={flowCode}
          onChange={(e) => setFlowCode(e.target.value)}
        />
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No open cases" hint="Cases assigned to you, or unassigned in your role's queue, appear here." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reference</th>
                <th className="table-head">Flow</th>
                <th className="table-head">State</th>
                <th className="table-head">Assignee</th>
                <th className="table-head">Escalation</th>
                <th className="table-head">SLA due</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">
                    <Link to={`/cases/${c.refNo}`} className="text-brand-600 underline">
                      {c.refNo}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{c.flowCode}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{c.assigneeId ?? 'unassigned'}</td>
                  <td className="table-cell">
                    {c.escalationLevel === 0 ? (
                      <span className="text-xs text-slate-400">on time</span>
                    ) : (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${
                          c.escalationLevel >= 2
                            ? 'bg-rose-50 text-rose-700 ring-rose-200'
                            : 'bg-amber-50 text-amber-700 ring-amber-200'
                        }`}
                      >
                        L{c.escalationLevel}
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-slate-500">{c.slaDueAt ? formatDateTime(c.slaDueAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
